/**
 * Suspended Account Screen
 *
 * Terminal screen shown when the API 403s the boot session fetch because the
 * user's account has been suspended (Road 6). Replaces the generic
 * connection-error / login-redirect fallback with a clear, dedicated message
 * and a path to contact support. Modeled on ConnectionErrorScreen's layout.
 */

const SUPPORT_EMAIL = 'support@xavior.ai'

interface SuspendedAccountScreenProps {
  email?: string
  onLogout: () => void
}

export function SuspendedAccountScreen({ email, onLogout }: SuspendedAccountScreenProps) {
  const supportHref = email
    ? `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        `Account suspended — ${email}`,
      )}`
    : `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Account suspended')}`

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#f8f9fc' }}>
      <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center border border-gray-100" style={{ maxWidth: '420px', boxShadow: '0 2px 24px rgba(0,0,0,0.06)' }}>
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
            <span className="text-white text-sm font-bold">X</span>
          </div>
          <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
        </div>

        <div className="text-4xl mb-4">🚫</div>
        <h2 className="text-2xl font-normal text-gray-900 mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
          Account Suspended
        </h2>
        <p className="text-sm text-gray-500 mb-1">
          {email ? (
            <>
              Access for <span className="font-medium text-gray-700">{email}</span> has been suspended.
            </>
          ) : (
            'Your account has been suspended.'
          )}
        </p>
        <p className="text-sm text-gray-400 mb-8">
          If you believe this is a mistake, please contact support and we'll help sort it out.
        </p>

        <div className="flex flex-col gap-3 justify-center">
          <a
            href={supportHref}
            className="w-full px-4 py-3 text-white font-medium rounded-xl transition-all text-sm inline-block"
            style={{ background: '#0ea5e9', boxShadow: '0 1px 3px rgba(14,165,233,0.3)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#0284c7'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(14,165,233,0.3)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#0ea5e9'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(14,165,233,0.3)' }}
          >
            Contact support
          </a>
          <button
            onClick={onLogout}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
